import React, { useState, useEffect } from 'react';
import Input from '../../../components/ui/Input';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { getChatCompletion } from '../../../services/aiIntegrations/chatCompletion';
import FIELD_SCHEMAS from './fieldSchemas';
import toast from 'react-hot-toast';
import { getPurchaseTypeIcon } from './utils';
import { searchMultipleQueries } from '../../../services/serpApiService';
import { calculateReviewScore, calculatePriceScore, getScoreStatus } from '../../project-comparison/utils/scoringMethodology';

/**
   * Fetch the og:image from a product URL via a CORS proxy.
   * Returns the image URL string or null if not found / fetch fails.
   * Uses exponential backoff retries per proxy with detailed error logging.
   * Results are cached in IndexedDB to prevent repeated proxy fetches.
   */

// Domains known to aggressively block public proxy services (WAF-protected luxury/high-end retailers).
// Fetching OG images from these will always timeout — skip straight to icon fallback.
const WAF_BLOCKED_DOMAINS = [
  // Luxury fashion
  'gucci.com',
  'louisvuitton.com',
  'lv.com',
  'chanel.com',
  'hermes.com',
  'dior.com',
  'prada.com',
  'burberry.com',
  'versace.com',
  'givenchy.com',
  'balenciaga.com',
  'bottegaveneta.com',
  'valentino.com',
  'fendi.com',
  'celine.com',
  'loewe.com',
  'moncler.com',
  'balmain.com',
  'alexandermcqueen.com',
  'saintlaurent.com',
  'ysl.com',
  'jimmychoo.com',
  'manoloblahnik.com',
  'christianlouboutin.com',
  // Luxury watches
  'rolex.com',
  'patek.com',
  'audemarspiguet.com',
  'iwc.com',
  'breitling.com',
  'tagheuer.com',
  'omega.com',
  'cartier.com',
  'vacheron-constantin.com',
  'jaegers-lecoultre.com',
  'jaeger-lecoultre.com',
  'blancpain.com',
  'breguet.com',
  'hublot.com',
  'ulyssenardin.com',
  'panerai.com',
  'glashutteoriginal.com',
  'a-lange-soehne.com',
  // Watch retailers
  'swisswatchexpo.com',
  'bobswatches.com',
  'watchfinder.com',
  'watchfinder.co.uk',
  'chrono24.com',
  'tourneau.com',
  'govberg.com',
  'grayandsons.com',
  'jomashop.com',
  // High-end jewellery
  'tiffany.com',
  'harrywinston.com',
  'bvlgari.com',
  'bulgari.com',
  'vancleefarpels.com',
  'chopard.com',
  'graff.com',
  // High-end department stores / multi-brand luxury
  'net-a-porter.com',
  'mrporter.com',
  'farfetch.com',
  'matchesfashion.com',
  'ssense.com',
  'mytheresa.com',
  'bergdorfgoodman.com',
  'neimanmarcus.com',
  'saksfifthavenue.com',
  'harrods.com',
  'selfridges.com',
];

const isWafBlockedDomain = (url) => {
  try {
    const hostname = new URL(url)?.hostname?.toLowerCase()?.replace(/^www\./, '');
    return WAF_BLOCKED_DOMAINS?.some((blocked) => hostname === blocked || hostname?.endsWith(`.${blocked}`));
  } catch {
    return false;
  }
};

/**
 * Build category-specific coaching instructions for Perplexity.
 * Returns a string that is injected into the system prompt to guide
 * Perplexity toward higher-quality, more specific results per purchase type.
 */
const buildCategoryCoaching = (purchaseType) => {
  const pt = purchaseType?.toLowerCase();

  // ── Accessories ──────────────────────────────────────────────────────────
  if (pt === 'watches') {
    return `CATEGORY COACHING — Watches:
Think like a WatchSeeker or Hodinkee reviewer. CRITICAL: You MUST embed EVERY user-specified attribute verbatim into each search query — especially dial/face color (e.g. "green dial"), sex (e.g. "mens", "womens"), style (e.g. "dress watch", "dive watch"), brand, movement type (e.g. "automatic"), case size (e.g. "40mm"), band type (e.g. "leather strap"), and case material (e.g. "stainless steel"). If the user said "green face" or "green dial", every query MUST include "green dial" — never return watches with white, black, or blue dials. Distinguish between dress, sport, dive, pilot, and smartwatch categories. Note water resistance rating and lug width.`;
  }
  if (pt === 'sunglasses') {
    return `CATEGORY COACHING — Sunglasses:
Think like a Sunglass Hut or SportRx expert. CRITICAL: Embed ALL user-specified attributes verbatim — sex (e.g. "mens", "womens"), frame style (e.g. "aviator", "wayfarer"), lens type (e.g. "polarized"), frame material (e.g. "acetate"), and color. If the user specified a lens color or frame color, every query MUST include it. Distinguish between fashion, sport, and prescription-ready frames.`;
  }
  if (['bags', 'wallets', 'belts']?.includes(pt)) {
    return `CATEGORY COACHING — ${pt?.charAt(0)?.toUpperCase() + pt?.slice(1)}:
Think like a leather goods specialist. CRITICAL: Embed ALL user-specified attributes verbatim — sex (e.g. "mens", "womens"), color (e.g. "black", "tan"), material (e.g. "leather", "canvas"), style/type, brand, and size. If the user specified a color, every query MUST include that exact color. Distinguish between luxury, mid-range, and budget tiers.`;
  }
  if (pt?.startsWith('jewelry')) {
    return `CATEGORY COACHING — Jewelry:
Think like a Brilliant Earth or Mejuri gemologist. CRITICAL: Embed ALL user-specified attributes verbatim — sex (e.g. "womens", "mens"), metal/material (e.g. "white gold", "sterling silver"), stone type (e.g. "diamond", "sapphire"), style (e.g. "solitaire", "halo"), size, and brand. Never substitute a different metal or stone than what was specified.`;
  }

  // ── Tech & Electronics ──────────────────────────────────────────────────────
  if (['computers-laptops', 'tablets']?.includes(pt)) {
    return `CATEGORY COACHING — Computers & Tablets:
Think like a tech reviewer at The Verge or Wirecutter. Prioritize current-generation models (within the last 12 months). Lead with the processor generation (e.g. Intel Core Ultra 7, Apple M4, Snapdragon X Elite), RAM, storage type (SSD/NVMe), and display quality. Distinguish between ultrabooks, gaming laptops, and workstations. Highlight real-world battery life in hours. Include at least one value pick and one premium pick. Avoid discontinued or hard-to-find models.`;
  }
  if (pt === 'smartphones') {
    return `CATEGORY COACHING — Smartphones:
Think like a GSMArena or MKBHD review. Lead with the chipset, camera system (megapixels, aperture, night mode quality), battery capacity and charging speed, and display type (OLED/LTPO). Distinguish flagship vs mid-range vs budget tiers. Note carrier compatibility and 5G bands. Include at least one Android and one iOS option unless the user specified a platform.`;
  }
  if (['cameras']?.includes(pt)) {
    return `CATEGORY COACHING — Cameras:
Think like a DPReview expert. Specify sensor size (full-frame, APS-C, Micro Four Thirds), megapixel count, autofocus system (phase-detect, eye-tracking), video capabilities (4K/6K, frame rates), and whether it's mirrorless or DSLR. Distinguish between beginner, enthusiast, and professional tiers. Note lens ecosystem compatibility.`;
  }
  if (['tv-home-theater']?.includes(pt)) {
    return `CATEGORY COACHING — TVs & Home Theater:
Think like an RTINGS.com reviewer. Lead with panel technology (OLED, QLED, Mini-LED, LCD), resolution (4K/8K), HDR support (Dolby Vision, HDR10+), refresh rate, and smart TV OS. Note screen size options and viewing distance recommendations. For home theater, distinguish between soundbars, AV receivers, and full surround systems.`;
  }
  if (['audio-equipment', 'headphones']?.includes(pt)) {
    return `CATEGORY COACHING — Audio Equipment & Headphones:
Think like a SoundGuys or Head-Fi reviewer. Specify driver type (dynamic, planar magnetic, balanced armature), frequency response, impedance, and whether it's open-back or closed-back for headphones. For speakers, note driver configuration, power output, and connectivity (Bluetooth, Wi-Fi, wired). Distinguish between studio/reference, audiophile, and consumer tiers.`;
  }
  if (['smart-home-devices']?.includes(pt)) {
    return `CATEGORY COACHING — Smart Home Devices:
Think like a SmartHomeReviewer. Lead with ecosystem compatibility (Amazon Alexa, Google Home, Apple HomeKit, Matter). Specify connectivity protocol (Wi-Fi, Zigbee, Z-Wave, Thread). Note setup complexity, hub requirements, and privacy/data practices. Distinguish between starter kits and advanced setups.`;
  }
  if (['wearables']?.includes(pt)) {
    return `CATEGORY COACHING — Wearables & Smartwatches:
Think like a Wareable or DC Rainmaker reviewer. Lead with health sensors (ECG, blood oxygen, skin temperature, continuous HR), GPS accuracy, battery life in days, and platform compatibility (iOS/Android). Distinguish between fitness trackers, sport watches, and lifestyle smartwatches.`;
  }
  if (['major-appliances']?.includes(pt)) {
    return `CATEGORY COACHING — Major Appliances:
Think like a Consumer Reports appliance expert. Lead with energy efficiency rating (Energy Star, kWh/year), capacity (cubic feet, load size), cycle options, and noise level (dB). Distinguish between budget, mid-range, and premium tiers. Note installation requirements (gas vs electric, 240V).`;
  }
  if (['furniture']?.includes(pt)) {
    return `CATEGORY COACHING — Furniture:
Think like an interior designer with a practical eye. Lead with dimensions (exact W×D×H), material and frame construction (solid wood, engineered wood, metal), upholstery type and durability (performance fabric, leather, velvet), and assembly requirements. Spread results across price tiers.`;
  }
  if (['mattresses']?.includes(pt)) {
    return `CATEGORY COACHING — Mattresses:
Think like a Sleepopolis or Mattress Clarity reviewer. Lead with construction type (memory foam, hybrid, innerspring, latex), firmness level (1-10 scale), pressure relief zones, motion isolation, and edge support. Note cooling technology for hot sleepers. Include trial period and return policy.`;
  }
  if (['home-gym']?.includes(pt)) {
    return `CATEGORY COACHING — Home Gym Equipment:
Think like a Garage Gym Reviews expert. Lead with weight capacity, footprint dimensions, adjustability range, and build quality (gauge steel, welds). Distinguish between cardio (treadmill, rower, bike) and strength (power rack, dumbbells, cable machine).`;
  }
  if (['home-security']?.includes(pt)) {
    return `CATEGORY COACHING — Home Security Systems:
Think like a SafeWise reviewer. Lead with monitoring type (professional 24/7, self-monitored, or hybrid), camera resolution and field of view, local vs cloud storage, and smart home integration. Note contract requirements vs no-contract options.`;
  }
  if (['lighting']?.includes(pt)) {
    return `CATEGORY COACHING — Lighting:
Think like a Lighting Design expert or Apartment Therapy reviewer. Lead with fixture type (pendant, flush mount, floor lamp, sconce, under-cabinet), lumens and color temperature (warm 2700K vs cool 5000K), dimmability, and smart home compatibility. Distinguish between accent, task, and ambient lighting. Note bulb type included or required.`;
  }
  if (['rugs']?.includes(pt)) {
    return `CATEGORY COACHING — Rugs:
Think like a Ruggable or Wayfair style expert. Lead with exact dimensions (e.g. 5x8, 8x10, runner), material and construction (hand-knotted wool, machine-made synthetic, flatweave, shag), pile height, and durability for the intended use (high-traffic, pet-friendly, outdoor). Note care instructions and pad requirements.`;
  }
  if (['decor']?.includes(pt)) {
    return `CATEGORY COACHING — Home Decor:
Think like an interior stylist. Lead with item type (wall art, throw pillows, vases, candles, mirrors, sculptures), dimensions, material quality, and style cohesion (modern, bohemian, minimalist, traditional). Spread results across price tiers. Note whether items are sold individually or as sets.`;
  }
  if (['car-purchase']?.includes(pt)) {
    return `CATEGORY COACHING — Car Purchase:
Think like a Car and Driver or Edmunds expert. Lead with powertrain (engine displacement, horsepower, torque, or EV range/charging speed), safety ratings (NHTSA/IIHS), fuel economy (MPG or MPGe), and key technology features (ADAS, infotainment). Distinguish between trim levels.`;
  }
  if (['motorcycle']?.includes(pt)) {
    return `CATEGORY COACHING — Motorcycles:
Think like a Cycle World reviewer. Lead with engine displacement (cc), power output (hp/torque), seat height, wet weight, and riding style category (sport, cruiser, adventure, naked, touring). Note ABS availability and traction control.`;
  }
  if (['tires']?.includes(pt)) {
    return `CATEGORY COACHING — Tires:
Think like a TireRack expert. Lead with tire size (e.g. 225/65R17), season rating (all-season, summer, winter), treadwear rating (UTQG), speed rating, and load index. Distinguish between touring, performance, all-terrain, and mud-terrain categories.`;
  }
  if (['home-purchase', 'home-refinance']?.includes(pt)) {
    return `CATEGORY COACHING — Home Purchase / Refinance:
Think like a Bankrate or NerdWallet mortgage expert. Lead with current APR (not just rate), loan types (30yr fixed, 15yr fixed, ARM, FHA, VA, USDA), minimum credit score requirements, and down payment minimums.`;
  }
  if (['credit-cards']?.includes(pt)) {
    return `CATEGORY COACHING — Credit Cards:
Think like a The Points Guy or NerdWallet credit card analyst. Lead with APR range, annual fee, sign-up bonus (points/miles/cash back), ongoing rewards rate per category (dining, travel, groceries), and key perks (lounge access, travel credits, purchase protection).`;
  }
  if (['engagement-rings']?.includes(pt)) {
    return `CATEGORY COACHING — Engagement Rings:
Think like a Brilliant Earth or Ritani gemologist. Lead with the 4 Cs (cut grade, color grade, clarity grade, carat weight), metal type (platinum, 14k/18k white/yellow/rose gold), setting style (solitaire, halo, pave, three-stone), and certification (GIA, AGS, IGI).`;
  }
  if (['concerts', 'sporting-events', 'events']?.includes(pt)) {
    return `CATEGORY COACHING — Tickets & Events:
Think like a StubHub or SeatGeek expert. Lead with specific event name, venue, date(s), and section/seating tier options. Distinguish between face-value (official) and secondary market pricing.`;
  }
  if (['cruises']?.includes(pt)) {
    return `CATEGORY COACHING — Cruises:
Think like a Cruise Critic expert. Lead with cruise line tier (luxury, premium, mainstream), itinerary highlights (ports of call, sea days), ship size and passenger capacity, cabin category (interior, ocean view, balcony, suite), and what's included vs extra cost.`;
  }
  if (['software', 'saas', 'digital-services', 'subscriptions']?.includes(pt)) {
    return `CATEGORY COACHING — Software & Digital Services:
Think like a G2 or Capterra analyst. Lead with pricing model (per-seat, flat monthly, usage-based), free trial availability, key feature differentiators, integration ecosystem, and customer support quality.`;
  }
  if (['medical-equipment']?.includes(pt)) {
    return `CATEGORY COACHING — Medical Equipment:
Think like a medical supply specialist. Lead with FDA clearance/510(k) status, clinical accuracy specifications, warranty and service options, and insurance/FSA/HSA eligibility.`;
  }
  if (['college']?.includes(pt)) {
    return `CATEGORY COACHING — Colleges & Universities:
Think like a Niche.com or US News college counselor. Lead with acceptance rate, average net price after aid, graduation rate, student-to-faculty ratio, and notable programs/rankings in the user's field of interest.`;
  }
  if (['bootcamps', 'certifications', 'online-learning', 'professional-training', 'courses']?.includes(pt)) {
    return `CATEGORY COACHING — Online Learning & Bootcamps:
Think like a SwitchUp or Course Report reviewer. Lead with curriculum depth, instructor credentials, time commitment (hours/week, total weeks), job placement rate (for bootcamps), and cost vs alternatives.`;
  }

  // ── Default fallback ────────────────────────────────────────────────────────
  return `CATEGORY COACHING — ${purchaseType}:
Think like a specialist buyer's guide author. Lead with the most decision-critical specifications for this product type. Distinguish between budget, mid-range, and premium tiers. Prioritize options with strong user reviews (4.0+ stars with 100+ reviews). Include at least one well-known brand and one value alternative.`;
};

/**
 * Extract the critical spec terms from the needs summary that MUST appear in
 * product titles/descriptions. Used for post-filtering SerpAPI results.
 * Returns an array of lowercase term strings (e.g. ["ryzen 9", "green dial", "mens"]).
 */
const extractMustMatchTerms = (needsSummary) => {
  const terms = [];
  const lines = needsSummary?.split('\n') || [];

  // ── Helper: add a term if non-empty and not already present ──────────────
  const addTerm = (t) => {
    let cleaned = t?.trim()?.toLowerCase();
    if (cleaned && !terms?.includes(cleaned)) terms?.push(cleaned);
  };

  // ── Parse structured spec lines (format: "- fieldId: value") ─────────────
  // These come directly from buildNeedsSummary and represent user-specified needs.
  // We extract the VALUE from each spec line and treat it as a must-match term
  // for visually-critical attributes (color, sex, style, material, brand, etc.).
  const VISUAL_SPEC_KEYS = new Set([
    // Accessories
    'color', 'sex', 'style', 'brand', 'bandtype', 'casematerial', 'material',
    'frameStyle', 'lenstype', 'framestyle', 'walletstyle', 'bagtype',
    'buckleStyle', 'buckleStyle',
    // Jewelry
    'materialpreference', 'stonetype', 'sourcingpreference',
    // Apparel / general
    'colorpreference', 'colorscheme', 'brandpreference',
    // Tech
    'operatingsystem', 'displaytype', 'resolution',
  ]);

  for (const line of lines) {
    const lower = line?.toLowerCase()?.trim();

    // Match "- key: value" lines from buildNeedsSummary
    const specLineMatch = lower?.match(/^-\s*([\w]+)\s*:\s*(.+)$/);
    if (specLineMatch) {
      const key = specLineMatch?.[1]?.toLowerCase()?.replace(/[^a-z]/g, '');
      const value = specLineMatch?.[2]?.trim();

      if (VISUAL_SPEC_KEYS?.has(key) && value && value !== 'no preference' && value !== 'unisex') {
        // For color: map "green face" / "green dial" → "green dial" for watches
        if (key === 'color') {
          // Normalize common watch face/dial descriptions
          const colorVal = value?.toLowerCase();
          if (colorVal?.includes('face') || colorVal?.includes('dial')) {
            // Extract the color word and append "dial"
            const colorWord = colorVal?.replace(/\b(face|dial|color)\b/g, '')?.trim();
            if (colorWord) addTerm(`${colorWord} dial`);
            addTerm(colorWord); // also add bare color
          } else {
            addTerm(colorVal);
          }
        } else if (key === 'sex') {
          // Normalize sex → search-friendly gender term
          const sexVal = value?.toLowerCase();
          if (sexVal === 'male') addTerm('mens');
          else if (sexVal === 'female') addTerm('womens');
          // Unisex: don't force-filter
        } else if (key === 'style') {
          // Only add style if it's a specific term (not generic like "casual")
          const styleVal = value?.toLowerCase();
          const genericStyles = new Set(['casual', 'classic', 'modern', 'standard', 'other']);
          if (!genericStyles?.has(styleVal)) addTerm(styleVal);
        } else if (key === 'brand' || key === 'brandpreference') {
          const brandVal = value?.toLowerCase();
          if (brandVal && brandVal !== 'no preference' && brandVal !== 'any') {
            addTerm(brandVal);
          }
        } else {
          addTerm(value?.toLowerCase());
        }
      }
      continue; // already handled this line
    }

    // ── Legacy: tech/electronics pattern matching (kept for backward compat) ──
    const techPatterns = [
      // AMD processor families
      /\b(ryzen\s*\d+)\b/,
      /\b(threadripper)\b/,
      /\b(epyc)\b/,
      // Intel processor families
      /\b(core\s*(?:ultra\s*)?\d+)\b/,
      /\b(core\s*i[3579](?:-\d+)?)\b/,
      /\b(xeon)\b/,
      // Apple silicon
      /\b(apple\s*m[1-9](?:\s*(?:pro|max|ultra))?)\b/,
      // GPU families
      /\b(rtx\s*\d{3,4}(?:\s*ti)?)\b/,
      /\b(rx\s*\d{3,4}(?:\s*xt)?)\b/,
      /\b(gtx\s*\d{3,4})\b/,
      // RAM
      /\b(\d+\s*gb\s*(?:ram|ddr\d)?)\b/,
      // Storage
      /\b(\d+\s*(?:gb|tb)\s*(?:ssd|nvme|hdd)?)\b/,
      // Screen size
      /\b(\d+(?:\.\d+)?\s*(?:inch|")\s*(?:display|screen|monitor)?)\b/,
      // Generic brand names that should be preserved
      /\b(amd|intel|nvidia|apple|samsung|lg|sony|dell|hp|lenovo|asus|acer|msi|razer|microsoft)\b/,
    ];

    for (const pattern of techPatterns) {
      const match = lower?.match(pattern);
      if (match?.[1]) {
        addTerm(match?.[1]);
      }
    }
  }

  return terms;
};

/**
 * Score a product result against must-match terms.
 * Returns { passes: boolean, matchedCount: number, totalTerms: number }
 *
 * Supports synonym matching for common accessory terms:
 *   "green dial" ↔ "green face" ↔ "green" *"mens"↔ "men's" ↔ "male" ↔ "men" *"womens" ↔ "women's" ↔ "female" ↔ "ladies"
 */
const scoreProductMatch = (product, mustMatchTerms) => {
  if (!mustMatchTerms?.length) return { passes: true, matchedCount: 0, totalTerms: 0 };

  const haystack = [
    product?.name,
    product?.description,
    product?.keySpecs,
    product?.vendor,
  ]
    ?.filter(Boolean)
    ?.join(' ')
    ?.toLowerCase();

  // Synonym groups: if ANY synonym matches, the term is considered matched
  const SYNONYM_GROUPS = [
    // Watch face/dial color synonyms
    ['dial', 'face'],
    // Gender synonyms
    ['mens', "men\'s", 'male', 'men', 'gents'],
    ['womens', "women\'s", 'female', 'women', 'ladies'],
    // Material synonyms
    ['stainless steel', 'stainless', 'ss'],
    ['leather', 'leather strap', 'leather band'],
    ['automatic', 'auto', 'self-winding'],
  ];

  const expandWithSynonyms = (term) => {
    const variants = [term];
    // For color+dial terms like "green dial", also try "green face", "green"
    const dialMatch = term?.match(/^(.+?)\s+dial$/);
    if (dialMatch) {
      variants?.push(`${dialMatch?.[1]} face`);
      variants?.push(dialMatch?.[1]); // bare color
    }
    const faceMatch = term?.match(/^(.+?)\s+face$/);
    if (faceMatch) {
      variants?.push(`${faceMatch?.[1]} dial`);
      variants?.push(faceMatch?.[1]);
    }
    // Check synonym groups
    for (const group of SYNONYM_GROUPS) {
      if (group?.some(s => term?.includes(s))) {
        group?.forEach(s => {
          if (!variants?.includes(s)) variants?.push(s);
        });
      }
    }
    return variants;
  };

  let matchedCount = 0;
  for (const term of mustMatchTerms) {
    const variants = expandWithSynonyms(term?.toLowerCase());
    const matched = variants?.some(v => haystack?.includes(v));
    if (matched) matchedCount++;
  }

  // Require ALL critical terms to match (strict mode)
  const passes = matchedCount === mustMatchTerms?.length;
  return { passes, matchedCount, totalTerms: mustMatchTerms?.length };
};

/**
 * Use Perplexity to generate smart, targeted Google Shopping search queries
 * based on the user's inputs. Returns an array of query strings.
 */
const generateSearchQueries = async (purchaseType, needsSummary, count = 3, excludeNames = []) => {
  const exclusionNote = excludeNames?.length > 0
    ? `\nDo NOT generate queries that would return these already-seen products: ${excludeNames?.slice(0, 10)?.join(', ')}.`
    : '';

  // Extract critical spec terms to embed in every query
  const mustTerms = extractMustMatchTerms(needsSummary);

  // Build a direct spec embedding instruction listing each must-match term explicitly
  const mustTermsNote = mustTerms?.length > 0
    ? `\n\nCRITICAL — MANDATORY SPEC EMBEDDING: Every single query you generate MUST contain ALL of these exact terms verbatim: ${mustTerms?.map(t => `"${t}"`)?.join(', ')}. Do NOT substitute, abbreviate, or omit any of these terms. Examples: if the user specified "green dial", every query MUST include "green dial" — never "white dial" or "black dial". If the user specified "mens", every query MUST include "mens". If the user specified "automatic", every query MUST include "automatic". Violating this rule will return wrong products that don't match the user's needs.`
    : '';

  // Also extract raw spec lines to give Perplexity direct context about what attributes to embed
  const specLines = needsSummary
    ?.split('\n')
    ?.filter(l => l?.trim()?.startsWith('-'))
    ?.filter(l => !l?.toLowerCase()?.includes('pricemax') && !l?.toLowerCase()?.includes('pricemin') && !l?.toLowerCase()?.includes('weightings'))
    ?.join('\n');

  const specEmbedNote = specLines
    ? `\n\nUSER SPECS TO EMBED IN QUERIES:\n${specLines}\nEach query MUST reflect these specs. For example, if color is "green", include "green dial" or "green" in every query. If sex is "Male", include "mens" in every query.`
    : '';

  const systemPrompt = `You are a product search query specialist. Your job is to generate highly targeted Google Shopping search queries that will return the best real products for a user's specific needs.
${buildCategoryCoaching(purchaseType)}
Return ONLY a valid JSON object with no markdown, no explanation, no code fences.
The JSON must have this exact structure:
{
  "queries": [
    "specific product search query 1",
    "specific product search query 2",
    "specific product search query 3"
  ]
}
Each query should:
- Be specific enough to return relevant products (include brand, model type, key specs when known)
- Be diverse — target different price tiers, brands, or product variants
- Be optimized for Google Shopping (product-focused, not question-based)
- Include ALL user-specified attributes EXACTLY as specified — color, sex/gender, style, material, brand, movement type, etc.
- Be 3-8 words long for best results${mustTermsNote}${specEmbedNote}${exclusionNote}`;

  const userMessage = `Generate ${count} targeted Google Shopping search queries for this purchase:\n\n${needsSummary}\n\nReturn only the JSON object.`;

  const response = await getChatCompletion(
    'PERPLEXITY','perplexity/sonar-pro',
    [
      { role: 'system', content: systemPrompt },
      { role: 'user', content: userMessage }
    ],
    { max_tokens: 500, temperature: 0.4 }
  );

  const content = response?.choices?.[0]?.message?.content || response;
  if (typeof content === 'string') {
    let cleaned = content?.replace(/<think>[\s\S]*?<\/think>/gi, '')?.trim();
    cleaned = cleaned?.replace(/```json\s*/gi, '')?.replace(/```\s*/g, '')?.trim();
    const jsonMatch = cleaned?.match(/\{[\s\S]*\}/);
    if (!jsonMatch) return [];
    const parsed = JSON.parse(jsonMatch?.[0]);
    return parsed?.queries || [];
  }
  return content?.queries || [];
};

const OptionsManagement = ({ formData, updateFormData, errors }) => {
  const purchaseType = formData?.basics?.purchaseType || '';
  const schema = FIELD_SCHEMAS?.[purchaseType];
  const schemaFields = schema?.fields || [];
  const requiredFieldIds = schema?.required || [];

  // Build initial dynamic fields state from schema, pre-populated from formData.specs
  const buildInitialDynamicFields = () => {
    const fields = {};
    const specs = formData?.specs || {};
    schemaFields?.forEach(f => {
      fields[f.id] = specs?.[f?.id] != null && String(specs?.[f?.id])?.trim() !== ''
        ? String(specs?.[f?.id])
        : '';
    });
    return fields;
  };

  const [newOption, setNewOption] = useState({
    name: '',
    website: '',
    price: '',
    notes: '',
    ...buildInitialDynamicFields()
  });

  // Reset dynamic fields when purchaseType changes, pre-populating from specs
  useEffect(() => {
    const specs = formData?.specs || {};
    const dynamicFields = {};
    schemaFields?.forEach(f => {
      dynamicFields[f.id] = specs?.[f?.id] != null && String(specs?.[f?.id])?.trim() !== ''
        ? String(specs?.[f?.id])
        : '';
    });
    setNewOption({
      name: '',
      website: '',
      price: '',
      notes: '',
      ...dynamicFields
    });
  }, [purchaseType]);

  const [isFindingOptions, setIsFindingOptions] = useState(false);
  const [aiSuggestedOptions, setAiSuggestedOptions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isRollingNew, setIsRollingNew] = useState(false);
  const [allProposedOptions, setAllProposedOptions] = useState([]);
  const [isManualFormOpen, setIsManualFormOpen] = useState(false);
  const [addedSuggestedOptions, setAddedSuggestedOptions] = useState(new Set());
  const [hoveredScoreIdx, setHoveredScoreIdx] = useState(null);
  const [searchStatusMessage, setSearchStatusMessage] = useState('');

  const handleAddOption = () => {
    if (!newOption?.name?.trim()) return;

    const dynamicSpecs = {};
    schemaFields?.forEach(f => {
      if (newOption?.[f?.id] != null && String(newOption?.[f?.id])?.trim() !== '') {
        dynamicSpecs[f.id] = newOption?.[f?.id];
      }
    });

    const option = {
      id: Date.now(),
      name: newOption?.name,
      website: newOption?.website || '',
      productUrl: newOption?.website || '',
      price: newOption?.price || '',
      notes: newOption?.notes || '',
      needsSnapshot: { ...(formData?.specs || {}), ...dynamicSpecs },
      dateAdded: new Date()?.toISOString()
    };

    const updatedOptions = [...(formData?.options || []), option];
    updateFormData('options', updatedOptions);

    setNewOption({
      name: '',
      website: '',
      price: '',
      notes: '',
      ...buildInitialDynamicFields()
    });
  };

  const handleRemoveOption = (optionId) => {
    const updatedOptions = formData?.options?.filter(option => option?.id !== optionId);
    updateFormData('options', updatedOptions);
  };

  const handleEditOption = (optionId, field, value) => {
    const updatedOptions = formData?.options?.map(option =>
      option?.id === optionId ? { ...option, [field]: value } : option
    );
    updateFormData('options', updatedOptions);
  };

  const validatePrice = (price) => {
    const priceRegex = /^\d+(\.\d{1,2})?$/;
    return priceRegex?.test(price) || price === '';
  };

  const areRequiredFieldsFilled = () => {
    return requiredFieldIds?.every(id => {
      const val = newOption?.[id];
      return val != null && String(val)?.trim() !== '';
    });
  };

  const isAddButtonDisabled =
    !newOption?.name?.trim() ||
    (newOption?.price && !validatePrice(newOption?.price)) ||
    !areRequiredFieldsFilled();

  const buildNeedsSummary = () => {
    const specs = formData?.specs || {};
    const budget = formData?.basics?.budgetRange || '';
    const zipCode = formData?.basics?.zipCode || '';
    const notes = formData?.basics?.notes || '';

    const specLines = Object.entries(specs)
      ?.filter(([key, val]) => key !== 'weightings' && val != null && String(val)?.trim() !== '')
      ?.map(([key, val]) => `- ${key}: ${val}`);

    let summary = `Purchase Type: ${purchaseType}\n`;
    if (budget) summary += `Budget: ${budget}\n`;
    if (zipCode) summary += `Location/Zip: ${zipCode}\n`;
    if (notes) summary += `Additional Notes: ${notes}\n`;
    if (specLines?.length > 0) {
      summary += `\nProduct Requirements:\n${specLines?.join('\n')}`;
    }
    return summary;
  };

  /**
   * Parse a price constraint field (e.g. "$500", "500", "1,200.00") into a number.
   * Returns null if the value is empty or cannot be parsed.
   */
  const parsePriceConstraint = (value) => {
    if (value == null || String(value)?.trim() === '') return null;
    let cleaned = String(value)?.replace(/[^0-9.]/g, '');
    const num = parseFloat(cleaned);
    return isNaN(num) ? null : num;
  };

  /**
   * Hard-filter a list of product results against price constraints.
   * Products with a parseable price that exceeds maxPrice or falls below minPrice are removed.
   * Products with no parseable price are kept (price unconfirmed).
   */
  const filterByPriceConstraints = (products, minPrice, maxPrice) => {
    if (minPrice == null && maxPrice == null) return products;
    return products?.filter((product) => {
      const rawPrice = product?.price;
      if (rawPrice == null || String(rawPrice)?.trim() === '') return true; // keep if no price
      const price = parseFloat(String(rawPrice)?.replace(/[^0-9.]/g, ''));
      if (isNaN(price)) return true; // keep if unparseable
      if (maxPrice != null && price > maxPrice) {
        console.log(`[Options Pipeline] Price filter: dropped "${product?.name}" ($${price} > max $${maxPrice})`);
        return false;
      }
      if (minPrice != null && price < minPrice) {
        console.log(`[Options Pipeline] Price filter: dropped "${product?.name}" ($${price} < min $${minPrice})`);
        return false;
      }
      return true;
    });
  };

  // ─── Score helpers for shopping result cards ─────────────────────────────────

/**
 * Calculate an overall score (0–100) for a suggested option based on available data.
 * Returns { overall, breakdown } where breakdown is an array of { label, score, note }.
 */
const calculateOptionScore = (opt, allOptions, formSpecs = {}) => {
  const breakdown = [];

  // 1. Price score
  const allPrices = allOptions
    ?.map(o => parseFloat(String(o?.price || '')?.replace(/[^0-9.]/g, '')))
    ?.filter(p => !isNaN(p) && p > 0);
  const optPrice = parseFloat(String(opt?.price || '')?.replace(/[^0-9.]/g, ''));

  if (!isNaN(optPrice) && optPrice > 0 && allPrices?.length > 0) {
    const budgetRaw = formSpecs?.priceMax || formSpecs?.budget || formSpecs?.totalBudget || formSpecs?.purchasePrice;
    const userBudget = budgetRaw ? parseFloat(String(budgetRaw)?.replace(/[^0-9.]/g, '')) || null : null;
    const priceScore = calculatePriceScore(optPrice, allPrices, userBudget);
    const minPrice = Math.min(...allPrices);
    const isCheapest = optPrice === minPrice;
    const pctAbove = minPrice > 0 ? Math.round(((optPrice - minPrice) / minPrice) * 100) : 0;
    let priceNote = `$${optPrice?.toLocaleString()}`;
    if (isCheapest) priceNote += ' — best price';
    else if (pctAbove > 0) priceNote += ` — ${pctAbove}% above lowest`;
    if (userBudget && optPrice > userBudget) priceNote += ' (over budget)';
    breakdown?.push({ label: 'Price', icon: 'DollarSign', score: priceScore, note: priceNote });
  }

  // 2. Review / rating score
  const reviewScoreRaw = opt?.reviewScore != null ? parseFloat(opt?.reviewScore) : null;
  const reviewMaxRaw = opt?.reviewMaxScore != null ? parseFloat(opt?.reviewMaxScore) : 5;
  const reviewCountRaw = opt?.reviewCount != null ? parseInt(String(opt?.reviewCount)?.replace(/[^0-9]/g, ''), 10) : 0;

  if (reviewScoreRaw != null && !isNaN(reviewScoreRaw) && reviewScoreRaw > 0) {
    const allReviewCounts = allOptions
      ?.map(o => parseInt(String(o?.reviewCount || '0')?.replace(/[^0-9]/g, ''), 10))
      ?.filter(c => !isNaN(c));
    const normalizedRating = reviewMaxRaw > 0 ? (reviewScoreRaw / reviewMaxRaw) * 5 : reviewScoreRaw;
    const rScore = calculateReviewScore(normalizedRating, reviewCountRaw, allReviewCounts);
    const reviewNote = `${reviewScoreRaw}/${reviewMaxRaw}★${reviewCountRaw ? ` (${reviewCountRaw?.toLocaleString()} reviews)` : ''}`;
    breakdown?.push({ label: 'Reviews', icon: 'Star', score: rScore, note: reviewNote });
  }

  // 3. Spec match score — compare keySpecs/description against user requirements
  const specEntries = Object.entries(formSpecs || {})
    ?.filter(([key, val]) => key !== 'weightings' && val != null && String(val)?.trim() !== '');

  if (specEntries?.length > 0) {
    const optText = [opt?.name || '', opt?.description || '', opt?.keySpecs || '']
      ?.join(' ')
      ?.toLowerCase();

    let matchCount = 0;
    specEntries?.forEach(([, val]) => {
      const reqWords = String(val)?.toLowerCase()?.split(/[\s,/-]+/)?.filter(w => w?.length > 2);
      const matched = reqWords?.filter(w => optText?.includes(w))?.length;
      if (reqWords?.length > 0 && matched / reqWords?.length >= 0.4) matchCount++;
    });

    const specMatchScore = specEntries?.length > 0
      ? Math.round(50 + (matchCount / specEntries?.length) * 50)
      : 70;

    const specNote = matchCount === specEntries?.length
      ? 'Matches all your requirements'
      : matchCount > 0
        ? `Matches ${matchCount}/${specEntries?.length} requirements`
        : 'Limited spec data available';

    breakdown?.push({ label: 'Spec Match', icon: 'CheckCircle', score: specMatchScore, note: specNote });
  }

  if (!breakdown?.length) return null;

  // Weighted overall: price 40%, reviews 35%, spec match 25%
  const weights = { Price: 0.4, Reviews: 0.35, 'Spec Match': 0.25 };
  let totalWeight = 0;
  let weightedSum = 0;
  breakdown?.forEach(item => {
    const w = weights?.[item?.label] || (1 / breakdown?.length);
    weightedSum += item?.score * w;
    totalWeight += w;
  });
  const overall = totalWeight > 0 ? Math.round(weightedSum / totalWeight) : 0;

  return { overall, breakdown };
};

const getScoreRingColor = (score) => {
  if (score >= 90) return '#22c55e';
  if (score >= 75) return '#3b82f6';
  if (score >= 60) return '#f59e0b';
  return '#ef4444';
};

const getScorePillStyle = (status) => {
  switch (status) {
    case 'excellent': return { bg: '#f0fdf4', text: '#15803d', border: '#bbf7d0' };
    case 'good':      return { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' };
    case 'fair':      return { bg: '#fffbeb', text: '#b45309', border: '#fde68a' };
    case 'poor':      return { bg: '#fef2f2', text: '#b91c1c', border: '#fecaca' };
    default:          return { bg: '#f9fafb', text: '#6b7280', border: '#e5e7eb' };
  }
};

/**
 * Hover tooltip showing score breakdown for a shopping result card.
 */
const ScoreTooltip = ({ scoreData, visible }) => {
  if (!visible || !scoreData) return null;
  const { overall, breakdown } = scoreData;
  const overallStatus = getScoreStatus(overall);
  const overallPill = getScorePillStyle(overallStatus);
  const scoreColor = getScoreRingColor(overall);

  return (
    <div
      className="absolute z-50 right-0 bottom-full mb-2 w-64 rounded-xl shadow-2xl pointer-events-none"
      style={{
        background: 'linear-gradient(135deg, #1e1b2e 0%, #2d1f3d 100%)',
        border: '1px solid rgba(245,138,84,0.3)',
        padding: '12px 14px',
      }}
    >
      {/* Arrow */}
      <div
        className="absolute right-4 top-full"
        style={{
          width: 0, height: 0,
          borderLeft: '6px solid transparent',
          borderRight: '6px solid transparent',
          borderTop: '6px solid #2d1f3d',
        }}
      />
      {/* Header */}
      <div className="flex items-center justify-between mb-3 pb-2" style={{ borderBottom: '1px solid rgba(255,255,255,0.12)' }}>
        <span className="text-xs font-bold text-white">Score Breakdown</span>
        <span
          className="text-xs font-bold px-2 py-0.5 rounded-full"
          style={{ background: overallPill?.bg, color: overallPill?.text }}
        >
          {overall}/100
        </span>
      </div>
      {/* Breakdown rows */}
      <div className="space-y-2">
        {breakdown?.map((item, i) => {
          const itemColor = getScoreRingColor(item?.score);
          const itemStatus = getScoreStatus(item?.score);
          const itemPill = getScorePillStyle(itemStatus);
          return (
            <div key={i} className="flex items-start gap-2">
              <div
                className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5"
                style={{ background: 'rgba(245,138,84,0.25)', border: '1px solid rgba(245,138,84,0.4)' }}
              >
                <Icon name={item?.icon} size={10} color="#F58A54" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-1">
                  <span className="text-xs font-semibold text-white truncate">{item?.label}</span>
                  <span
                    className="text-xs font-bold px-1.5 py-0.5 rounded-full flex-shrink-0"
                    style={{ background: itemPill?.bg, color: itemPill?.text }}
                  >
                    {item?.score}%
                  </span>
                </div>
                <div className="mt-1 h-1 rounded-full bg-white/10 overflow-hidden">
                  <div
                    className="h-full rounded-full transition-all"
                    style={{ width: `${item?.score}%`, background: itemColor }}
                  />
                </div>
                {item?.note && (
                  <p className="text-xs mt-0.5 leading-relaxed" style={{ color: 'rgba(255,255,255,0.55)' }}>
                    {item?.note}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
      {/* Footer hint */}
      <p className="text-xs mt-3 pt-2" style={{ borderTop: '1px solid rgba(255,255,255,0.08)', color: 'rgba(255,255,255,0.4)' }}>
        Add to stack for full weighted comparison
      </p>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────

/**
 * Core pipeline: Perplexity generates queries → SerpAPI fetches real products.
 * Returns an array of normalized product options ready for display.
 */
const runSearchPipeline = async (needsSummary, excludeNames = [], queryCount = 3) => {
    // Credit cards: skip Google Shopping entirely — use Perplexity directly
    if (purchaseType === 'credit-cards') {
      console.log('[Options Pipeline] Credit cards detected — skipping SerpAPI, using Perplexity directly');
      setSearchStatusMessage('Fetching AI credit card recommendations...');

      const systemPrompt = `You are a credit card research assistant. The user is looking for credit cards.
${buildCategoryCoaching(purchaseType)}
Return ONLY a valid JSON object with no markdown, no explanation, no code fences.
The JSON must have this exact structure:
{
  "options": [
    {
      "name": "Card Name (e.g. Chase Sapphire Preferred)",
      "vendor": "Issuing Bank (e.g. Chase)",
      "price": "95",
      "productUrl": "https://exact-card-application-page-url.com",
      "description": "Brief description of why this card matches the user\'s needs",
      "keySpecs": "APR range, annual fee, sign-up bonus, rewards rate",
      "reviewScore": "4.5",
      "reviewMaxScore": "5",
      "reviewCount": "1200"
    }
  ]
}
Provide 3-5 real credit card options. The price field should represent the annual fee as a number string without currency symbols (use "0" for no annual fee).`;

      const userMessage = `Find me the best credit cards for the following needs:\n\n${needsSummary}\n\nReturn only the JSON object.`;

      const response = await getChatCompletion(
        'PERPLEXITY',
        'perplexity/sonar-pro',
        [
          { role: 'system', content: systemPrompt },
          { role: 'user', content: userMessage }
        ],
        { max_tokens: 2000, temperature: 0.3 }
      );

      const content = response?.choices?.[0]?.message?.content || response;
      if (typeof content === 'string') {
        let cleaned = content?.replace(/<think>[\s\S]*?<\/think>/gi, '')?.trim();
        cleaned = cleaned?.replace(/```json\s*/gi, '')?.replace(/```\s*/g, '')?.trim();
        const jsonMatch = cleaned?.match(/\{[\s\S]*\}/);
        if (!jsonMatch) return [];
        const parsed = JSON.parse(jsonMatch?.[0]);
        return (parsed?.options || [])?.map(opt => ({ ...opt, priceUnconfirmed: true, priceVerified: false }));
      }
      return [];
    }

    // Step 1: Perplexity generates targeted search queries
    setSearchStatusMessage('Generating smart search queries...');
    const queries = await generateSearchQueries(purchaseType, needsSummary, queryCount, excludeNames);

    if (!queries?.length) {
      throw new Error('Could not generate search queries. Please try again.');
    }

    console.log('[Options Pipeline] Perplexity generated queries:', queries);

    // Extract price constraints from specs to enforce at both API and post-filter levels
    const specs = formData?.specs || {};
    const maxPriceConstraint = parsePriceConstraint(specs?.priceMax);
    const minPriceConstraint = parsePriceConstraint(specs?.priceMin);

    if (maxPriceConstraint != null) {
      console.log(`[Options Pipeline] Price constraint: max $${maxPriceConstraint}`);
    }
    if (minPriceConstraint != null) {
      console.log(`[Options Pipeline] Price constraint: min $${minPriceConstraint}`);
    }

    // Step 2: SerpAPI Google Shopping Lite executes queries → real products
    // Pass price constraints so SerpAPI pre-filters at the API level
    setSearchStatusMessage('Searching...');
    const serpResults = await searchMultipleQueries(queries, {
      maxResults: 15, // fetch a larger pool so post-filtering still leaves ~5
      ...(maxPriceConstraint != null && { maxPrice: maxPriceConstraint }),
      ...(minPriceConstraint != null && { minPrice: minPriceConstraint }),
    });

    if (serpResults?.length > 0) {
      console.log('[Options Pipeline] SerpAPI returned', serpResults?.length, 'products');

      // Step 3: Post-filter — hard-enforce price constraints (SerpAPI filters are not always exact)
      let filteredResults = filterByPriceConstraints(serpResults, minPriceConstraint, maxPriceConstraint);
      console.log(`[Options Pipeline] After price filter: ${filteredResults?.length}/${serpResults?.length} products within budget`);

      // Step 4: Post-filter — drop results that don't match critical spec terms
      const mustMatchTerms = extractMustMatchTerms(needsSummary);

      if (mustMatchTerms?.length > 0) {
        const strictMatches = filteredResults?.filter(p => scoreProductMatch(p, mustMatchTerms)?.passes);
        console.log(
          `[Options Pipeline] Post-filter: ${strictMatches?.length}/${filteredResults?.length} products passed strict spec match for terms: [${mustMatchTerms?.join(', ')}]`
        );

        if (strictMatches?.length >= 5) {
          // Enough strict matches — use only those, trimmed to 5
          filteredResults = strictMatches?.slice(0, 5);
        } else if (strictMatches?.length > 0) {
          // Too few strict matches — supplement with best partial matches to reach 5
          const partialMatches = filteredResults
            ?.filter(p => !scoreProductMatch(p, mustMatchTerms)?.passes)
            ?.sort((a, b) => {
              const aScore = scoreProductMatch(a, mustMatchTerms)?.matchedCount;
              const bScore = scoreProductMatch(b, mustMatchTerms)?.matchedCount;
              return bScore - aScore;
            });
          filteredResults = [...strictMatches, ...partialMatches]?.slice(0, 5);
          console.warn('[Options Pipeline] Supplementing with partial matches due to low strict match count');
        } else {
          // No strict matches at all — return price-filtered results trimmed to 5
          console.warn('[Options Pipeline] No strict spec matches found — returning price-filtered results. Queries may need refinement.');
          filteredResults = filteredResults?.slice(0, 5);
        }
      } else {
        // No must-match terms — just trim to 5
        filteredResults = filteredResults?.slice(0, 5);
      }

      return filteredResults;
    }

    // Fallback: if SerpAPI key not configured or returns nothing, use Perplexity directly
    // for product recommendations (with estimated prices clearly labeled)
    console.warn('[Options Pipeline] SerpAPI returned no results — falling back to Perplexity product recommendations');
    setSearchStatusMessage('Fetching AI product recommendations...');

    const systemPrompt = `You are a shopping research assistant. The user is looking to purchase a ${purchaseType}.
${buildCategoryCoaching(purchaseType)}
Return ONLY a valid JSON object with no markdown, no explanation, no code fences.
The JSON must have this exact structure:
{
  "options": [
    {
      "name": "Product/Service Name",
      "vendor": "Retailer or Seller Name",
      "price": "299.99",
      "productUrl": "https://exact-product-page-url.com/specific-product-path",
      "description": "Brief description of why this matches the user's needs",
      "keySpecs": "Key specifications relevant to the user's requirements",
      "reviewScore": "4.5",
      "reviewMaxScore": "5",
      "reviewCount": "1200"
    }
  ]
}
Provide 3-5 real options. Price should be a number string without currency symbols.${maxPriceConstraint != null ? ` IMPORTANT: All options MUST be priced at or below $${maxPriceConstraint}.` : ''}${minPriceConstraint != null ? ` All options MUST be priced at or above $${minPriceConstraint}.` : ''}`;

    const userMessage = `Find me the best online options for the following purchase:\n\n${needsSummary}\n\nReturn only the JSON object.`;

    const response = await getChatCompletion(
      'PERPLEXITY',
      'perplexity/sonar-pro',
      [
        { role: 'system', content: systemPrompt },
        { role: 'user', content: userMessage }
      ],
      { max_tokens: 2000, temperature: 0.3 }
    );

    const content = response?.choices?.[0]?.message?.content || response;
    if (typeof content === 'string') {
      let cleaned = content?.replace(/<think>[\s\S]*?<\/think>/gi, '')?.trim();
      cleaned = cleaned?.replace(/```json\s*/gi, '')?.replace(/```\s*/g, '')?.trim();
      const jsonMatch = cleaned?.match(/\{[\s\S]*\}/);
      if (!jsonMatch) return [];
      const parsed = JSON.parse(jsonMatch?.[0]);
      // Mark fallback results as unconfirmed price, then enforce price constraints
      const fallbackOptions = (parsed?.options || [])?.map(opt => ({ ...opt, priceUnconfirmed: true, priceVerified: false }));
      return filterByPriceConstraints(fallbackOptions, minPriceConstraint, maxPriceConstraint);
    }
    return [];
  };

  const handleFindOptions = async () => {
    setIsFindingOptions(true);
    setShowSuggestions(false);
    setAiSuggestedOptions([]);
    setSearchStatusMessage('');

    try {
      const needsSummary = buildNeedsSummary();
      const results = await runSearchPipeline(needsSummary, [], 3);

      if (!results?.length) {
        toast?.error('No options found. Try refining your needs on the previous step.');
        return;
      }

      setAiSuggestedOptions(results);
      setShowSuggestions(true);
      setAllProposedOptions(prev => [...(prev || []), ...results]);
    } catch (err) {
      console.error('Find Options error:', err);
      toast?.error('Failed to find options. Please try again.');
    } finally {
      setIsFindingOptions(false);
      setSearchStatusMessage('');
    }
  };

  const handleRollNewOptions = async () => {
    setIsRollingNew(true);
    setShowSuggestions(false);
    setAiSuggestedOptions([]);
    setSearchStatusMessage('');

    try {
      const needsSummary = buildNeedsSummary();
      const existingNames = [
        ...(formData?.options || [])?.map(o => o?.name),
        ...(allProposedOptions || [])?.map(o => o?.name),
      ]?.filter(Boolean);

      const results = await runSearchPipeline(needsSummary, existingNames, 4);

      if (!results?.length) {
        toast?.error('No new options found. Try refining your needs.');
        return;
      }

      // Filter out already-proposed options by name similarity
      const filtered = results?.filter(opt =>
        !allProposedOptions?.some(
          p => p?.name?.toLowerCase() === opt?.name?.toLowerCase()
        )
      );

      const finalResults = filtered?.length > 0 ? filtered : results;

      setAiSuggestedOptions(finalResults);
      setShowSuggestions(true);
      toast?.success(`Found ${finalResults?.length} new options!`);
      setAllProposedOptions(prev => [...(prev || []), ...results]);
    } catch (err) {
      console.error('Roll New Options error:', err);
      toast?.error('Failed to roll new options. Please try again.');
    } finally {
      setIsRollingNew(false);
      setSearchStatusMessage('');
    }
  };

  const handleAddSuggestedOption = (suggested, idx) => {
    const option = {
      id: Date.now() + Math.random(),
      name: suggested?.vendor ? `${suggested?.name} — ${suggested?.vendor}` : suggested?.name,
      website: suggested?.productUrl || '',
      productUrl: suggested?.productUrl || '',
      price: suggested?.price || '',
      notes: [suggested?.description, suggested?.keySpecs]?.filter(Boolean)?.join(' | '),
      needsSnapshot: formData?.specs || {},
      destination: suggested?.destination || '',
      imageUrl: suggested?.imageUrl || '',
      dateAdded: new Date()?.toISOString(),
      reviewScore: suggested?.reviewScore ? parseFloat(suggested?.reviewScore) : null,
      reviewMaxScore: suggested?.reviewMaxScore ? parseFloat(suggested?.reviewMaxScore) : null,
      reviewCount: suggested?.reviewCount ? parseInt(String(suggested?.reviewCount)?.replace(/[^0-9]/g, ''), 10) : null,
      priceVerified: suggested?.priceVerified || false,
      priceUnconfirmed: suggested?.priceUnconfirmed || false,
    };

    const updatedOptions = [...(formData?.options || []), option];
    updateFormData('options', updatedOptions);
    setAddedSuggestedOptions(prev => new Set([...prev, idx]));
    toast?.success(`"${suggested?.name}" added to your options!`);
  };

  const handleDismissSuggestions = () => {
    setShowSuggestions(false);
    setAiSuggestedOptions([]);
    setAddedSuggestedOptions(new Set());
  };

  const hasNeeds = () => {
    const specs = formData?.specs || {};
    return Object.entries(specs)?.some(
      ([key, val]) => key !== 'weightings' && val != null && String(val)?.trim() !== ''
    ) || formData?.basics?.purchaseType;
  };

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-1">Purchase Options</h3>
          <p className="text-text-secondary text-sm">Add different purchase options you're considering for comparison. Include pricing and website links for online purchases.</p>
        </div>
        <div className="flex-shrink-0">
          <Button
            variant="default"
            onClick={handleFindOptions}
            disabled={isFindingOptions || isRollingNew}
            iconName={isFindingOptions ? undefined : 'Search'}
            iconPosition="left"
            size="xl"
            className="whitespace-nowrap px-8 py-4 text-base"
            style={{ background: 'linear-gradient(to right, #F58A54, #F06F6B)', border: 'none', color: 'white' }}
          >
            {isFindingOptions ? (
              <span className="flex items-center gap-2">
                <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                </svg>
                Finding Options...
              </span>
            ) : 'Find Options'}
          </Button>
        </div>
      </div>
      {/* No needs warning */}
      {!hasNeeds() && (
        <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
          <Icon name="AlertCircle" size={16} className="flex-shrink-0" />
          <span>Complete the <strong>Needs</strong> step first so we can find relevant options for you.</span>
        </div>
      )}
      {/* AI Suggested Options */}
      {showSuggestions && aiSuggestedOptions?.length > 0 && (
        <div className="rounded-2xl overflow-hidden shadow-lg border border-orange-100">
          <div className="px-5 py-4 flex items-center justify-between" style={{ background: 'linear-gradient(135deg, #F06F6B 0%, #F58A54 50%, #F07A5E 100%)' }}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-xl bg-white/20 backdrop-blur-sm flex items-center justify-center">
                <Icon name="Sparkles" size={16} color="white" />
              </div>
              <div>
                <h4 className="font-bold text-white text-sm tracking-wide">Shopping Results</h4>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={handleRollNewOptions}
                disabled={isRollingNew}
                title="Roll 5 new options"
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/20 hover:bg-white/30 transition-colors"
              >
                {isRollingNew ? (
                  <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
                  </svg>
                ) : (
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <rect x="2" y="2" width="20" height="20" rx="3" ry="3"/>
                    <circle cx="8" cy="8" r="1.5" fill="currentColor" stroke="none"/>
                    <circle cx="16" cy="8" r="1.5" fill="currentColor" stroke="none"/>
                    <circle cx="8" cy="16" r="1.5" fill="currentColor" stroke="none"/>
                    <circle cx="16" cy="16" r="1.5" fill="currentColor" stroke="none"/>
                    <circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none"/>
                  </svg>
                )}
                <span className="text-white text-xs font-medium">{isRollingNew ? 'Rolling...' : 'Roll 5 New'}</span>
              </button>
              <button
                onClick={handleDismissSuggestions}
                className="w-7 h-7 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors"
              >
                <Icon name="X" size={14} color="white" />
              </button>
            </div>
          </div>

          <div className="bg-gray-50 p-4 space-y-3">
            {aiSuggestedOptions?.map((opt, idx) => {
              const scoreData = calculateOptionScore(opt, aiSuggestedOptions, formData?.specs);
              const scoreColor = scoreData ? getScoreRingColor(scoreData?.overall) : null;
              const scoreStatus = scoreData ? getScoreStatus(scoreData?.overall) : null;
              const scorePill = scoreData ? getScorePillStyle(scoreStatus) : null;

              return (
              <div key={idx} className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-visible">
                <div className="h-0.5 w-full" style={{ background: 'linear-gradient(to right, #F06F6B, #F58A54)' }} />
                <div className="p-4 flex items-start gap-3">
                  <div className="flex-shrink-0 w-7 h-7 rounded-lg flex items-center justify-center text-xs font-bold text-white" style={{ background: 'linear-gradient(135deg, #F06F6B, #F58A54)' }}>
                    {idx + 1}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex items-start gap-3 min-w-0">
                        {/* Product image from Google Shopping or fallback icon */}
                        {opt?.imageUrl ? (
                          <div className="flex-shrink-0 relative" style={{ zIndex: 'auto' }}>
                            <img
                              src={opt?.imageUrl}
                              alt={`${opt?.name || purchaseType} product image`}
                              className="w-24 h-24 rounded-lg object-cover border border-gray-100 transition-all duration-300 ease-in-out cursor-zoom-in hover:scale-[2.5] hover:shadow-2xl"
                              style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.08)', position: 'relative', zIndex: 1, transformOrigin: 'top left' }}
                              onMouseEnter={e => { e.currentTarget.style.zIndex = '9999'; e.currentTarget.style.position = 'relative'; }}
                              onMouseLeave={e => { e.currentTarget.style.zIndex = '1'; }}
                              onError={e => {
                                e.currentTarget.style.display = 'none';
                                const sibling = e?.currentTarget?.parentElement?.nextSibling;
                                if (sibling) sibling.style.display = 'flex';
                              }}
                            />
                          </div>
                        ) : null}
                        <div
                          className="flex-shrink-0 w-24 h-24 rounded-lg flex items-center justify-center text-white"
                          style={{
                            background: `hsl(${(idx * 67 + 200) % 360}, 55%, 48%)`,
                            display: opt?.imageUrl ? 'none' : 'flex'
                          }}
                        >
                          <Icon name={getPurchaseTypeIcon(purchaseType) || 'ShoppingCart'} size={36} color="white" />
                        </div>
                        <div className="min-w-0">
                          <p className="font-semibold text-gray-900 text-sm leading-tight">{opt?.name}</p>
                          {opt?.vendor && (
                            <p className="text-xs text-gray-400 mt-0.5 font-medium uppercase tracking-wide">{opt?.vendor}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-1 flex-shrink-0">
                        {opt?.price && (
                          <span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: 'linear-gradient(135deg, #FFF0EE, #FFF5F0)', color: '#F06F6B', border: '1px solid #FECDC8' }}>
                            ${opt?.price}
                          </span>
                        )}
                        {/* Overall Score Badge with hover tooltip */}
                        {scoreData && (
                          <div
                            className="relative"
                            onMouseEnter={() => setHoveredScoreIdx(idx)}
                            onMouseLeave={() => setHoveredScoreIdx(null)}
                          >
                            <button
                              type="button"
                              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full cursor-help transition-all hover:shadow-sm"
                              style={{
                                background: scorePill?.bg,
                                color: scorePill?.text,
                                border: `1px solid ${scorePill?.border}`,
                              }}
                            >
                              <div
                                className="w-3 h-3 rounded-full flex-shrink-0"
                                style={{
                                  background: `conic-gradient(${scoreColor} ${scoreData?.overall * 3.6}deg, rgba(0,0,0,0.08) 0deg)`,
                                }}
                              />
                              <span className="text-xs font-bold">{scoreData?.overall}</span>
                              <Icon name="ChevronDown" size={9} />
                            </button>
                            {hoveredScoreIdx === idx && (
                              <ScoreTooltip scoreData={scoreData} visible />
                            )}
                          </div>
                        )}
                        {/* Price source badge */}
                        {opt?.priceVerified ? (
                          <span className="inline-flex items-center gap-1 text-xs font-medium hover:underline transition-colors cursor-pointer">
                            <Icon name="CheckCircle" size={10} style={{ color: '#16a34a' }} />
                            Live price
                          </span>
                        ) : opt?.priceUnconfirmed ? (
                          <a
                            href={opt?.productUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title="Price is an AI estimate — click to verify on the product page"
                            className="inline-flex items-center gap-1 text-xs font-medium hover:underline transition-colors"
                          >
                            <Icon name="AlertTriangle" size={10} style={{ color: '#d97706' }} />
                            Verify price
                          </a>
                        ) : null}
                      </div>
                    </div>
                    {opt?.description && (
                      <p className="text-xs text-gray-500 mt-2 leading-relaxed line-clamp-2">{opt?.description}</p>
                    )}
                    {opt?.keySpecs && (
                      <div className="mt-2 flex items-start gap-1">
                        <Icon name="CheckCircle" size={11} className="flex-shrink-0 mt-0.5" style={{ color: '#F58A54' }} />
                        <p className="text-xs text-gray-400 italic line-clamp-1">{opt?.keySpecs}</p>
                      </div>
                    )}
                    {(opt?.reviewScore || opt?.reviewCount) && (
                      <div className="mt-2 flex items-center gap-1.5">
                        <Icon name="Star" size={12} style={{ color: '#F59E0B' }} fill="#F59E0B" />
                        {opt?.reviewScore && (
                          <span className="text-xs font-semibold text-foreground">
                            {opt?.reviewScore}{opt?.reviewMaxScore != null ? `/${opt?.reviewMaxScore}` : ''}
                          </span>
                        )}
                        {opt?.reviewCount != null && (
                          <span className="text-xs text-text-secondary">({Number(opt?.reviewCount)?.toLocaleString()} reviews)</span>
                        )}
                      </div>
                    )}
                    <div className="flex items-center justify-between mt-3">
                      {opt?.productUrl ? (
                        <a
                          href={opt?.productUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border transition-colors hover:bg-orange-50"
                          style={{ color: '#F06F6B', borderColor: '#F06F6B' }}
                        >
                          <Icon name="ExternalLink" size={14} />
                          View Product
                        </a>
                      ) : <span />}
                      <button
                        onClick={() => handleAddSuggestedOption(opt, idx)}
                        className="inline-flex items-center gap-1.5 text-xs font-semibold text-white px-3 py-1.5 rounded-lg transition-opacity hover:opacity-90 active:scale-95"
                        style={addedSuggestedOptions?.has(idx) ? { background: '#16a34a' } : { background: 'linear-gradient(135deg, #F06F6B, #F58A54)' }}
                      >
                        {addedSuggestedOptions?.has(idx) ? (
                          <>
                            <Icon name="Check" size={12} color="white" />
                            Added
                          </>
                        ) : (
                          <>
                            <Icon name="Plus" size={12} color="white" />
                            Add
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </div>
      )}
      {/* Add New Option Form */}
      <div className="bg-surface rounded-lg border border-border">
        <button
          type="button"
          onClick={() => setIsManualFormOpen((prev) => !prev)}
          className="w-full flex items-center justify-between p-4 text-left hover:bg-muted/30 transition-colors rounded-lg"
        >
          <h4 className="font-medium text-foreground flex items-center">
            <Icon name="Plus" size={16} className="mr-2" />
            Add Option Manually
          </h4>
          <Icon
            name={isManualFormOpen ? 'ChevronUp' : 'ChevronDown'}
            size={16}
            className="text-text-secondary"
          />
        </button>

        {isManualFormOpen && (
          <div className="px-6 pb-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Option Name"
                type="text"
                placeholder="e.g., Amazon Option, Local Store Option"
                value={newOption?.name}
                onChange={(e) => setNewOption({ ...newOption, name: e?.target?.value })}
                required
              />

              <Input
                label="Product URL / Website Link"
                type="url"
                placeholder="https://www.amazon.com/product-link"
                value={newOption?.website}
                onChange={(e) => setNewOption({ ...newOption, website: e?.target?.value })}
              />

              <Input
                label="Price (Optional)"
                type="text"
                placeholder="299.99"
                value={newOption?.price}
                onChange={(e) => setNewOption({ ...newOption, price: e?.target?.value })}
                error={newOption?.price && !validatePrice(newOption?.price) ? 'Please enter a valid price (e.g., 299.99)' : ''}
              />

              {/* Dynamic schema fields */}
              {schemaFields?.map((field) => {
                const isRequired = requiredFieldIds?.includes(field?.id);
                return (
                  <Input
                    key={field?.id}
                    label={field?.label}
                    type="text"
                    placeholder={field?.hint}
                    value={newOption?.[field?.id] || ''}
                    onChange={(e) => setNewOption({ ...newOption, [field?.id]: e?.target?.value })}
                    required={isRequired}
                  />
                );
              })}

              <div className={schemaFields?.length > 0 ? 'md:col-span-2' : 'md:col-span-2'}>
                <Input
                  label="Notes"
                  type="text"
                  placeholder="Any specific notes about this option..."
                  value={newOption?.notes}
                  onChange={(e) => setNewOption({ ...newOption, notes: e?.target?.value })}
                />
              </div>
            </div>

            {/* Required fields hint */}
            {requiredFieldIds?.length > 0 && (
              <p className="mt-3 text-xs text-text-secondary flex items-center gap-1">
                <Icon name="Info" size={12} />
                Fields marked <span className="text-error font-medium">*</span> are required before adding this option.
              </p>
            )}

            <div className="mt-4 flex justify-end">
              <Button
                variant="default"
                onClick={handleAddOption}
                disabled={isAddButtonDisabled}
                iconName="Plus"
                iconPosition="left"
              >
                Add Option
              </Button>
            </div>
          </div>
        )}
      </div>
      {/* Options List */}
      {formData?.options && formData?.options?.length > 0 && (
        <div className="space-y-4">
          <h4 className="font-medium text-foreground flex items-center">
            <Icon name="ShoppingCart" size={16} className="mr-2" />
            Added Options ({formData?.options?.length})
          </h4>

          <div className="space-y-3">
            {formData?.options?.map((option, optIdx) => (
              <div key={option?.id} className="bg-card rounded-lg p-4 border border-border">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-start gap-3 flex-1">
                    {/* Photo or fallback subcategory icon avatar */}
                    {option?.imageUrl ? (
                      <img
                        src={option?.imageUrl}
                        alt={`${option?.name || purchaseType} photo`}
                        className="flex-shrink-0 w-14 h-14 rounded-lg object-cover border border-gray-100 transition-transform duration-300 ease-in-out hover:scale-[5] hover:z-10 hover:shadow-xl"
                        style={{ boxShadow: '0 1px 4px rgba(0,0,0,0.08)', position: 'relative' }}
                        onError={e => {
                          e.currentTarget.style.display = 'none';
                          const fallback = e?.currentTarget?.nextSibling;
                          if (fallback) fallback.style.display = 'flex';
                        }}
                      />
                    ) : null}
                    <div
                      className="flex-shrink-0 w-14 h-14 rounded-lg flex items-center justify-center text-white"
                      style={{
                        background: `hsl(${(optIdx * 67 + 200) % 360}, 55%, 48%)`,
                        display: option?.imageUrl ? 'none' : 'flex'
                      }}
                    >
                      <Icon name={getPurchaseTypeIcon(purchaseType) || 'ShoppingCart'} size={26} color="white" />
                    </div>
                    <div className="flex-1">
                      <h5 className="font-medium text-foreground">{option?.name}</h5>
                      <div className="text-sm text-text-secondary space-y-1 mt-1">
                        {option?.price && (
                          <div className="flex items-center">
                            <Icon name="DollarSign" size={14} className="mr-2" />
                            <span>${option?.price}</span>
                          </div>
                        )}
                        {(option?.productUrl || option?.website) && (
                          <div className="flex items-center">
                            <Icon name="Globe" size={14} className="mr-2" />
                            <a
                              href={option?.productUrl || option?.website}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-primary hover:underline truncate max-w-xs"
                            >
                              {option?.productUrl || option?.website}
                            </a>
                          </div>
                        )}
                        {(option?.reviewScore != null || option?.reviewCount != null) && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <Icon name="Star" size={13} style={{ color: '#F59E0B' }} fill="#F59E0B" />
                            {option?.reviewScore != null && (
                              <span className="text-sm font-semibold text-foreground">
                                {option?.reviewScore}{option?.reviewMaxScore != null ? `/${option?.reviewMaxScore}` : ''}
                              </span>
                            )}
                            {option?.reviewCount != null && (
                              <span className="text-xs text-text-secondary">({Number(option?.reviewCount)?.toLocaleString()} reviews)</span>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => handleRemoveOption(option?.id)}
                    className="text-error hover:text-error hover:bg-error/10"
                  >
                    <Icon name="Trash2" size={16} />
                  </Button>
                </div>

                {option?.notes && (
                  <div className="text-sm text-text-secondary bg-muted rounded p-2">
                    <strong>Notes:</strong> {option?.notes}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
      {/* Validation Error */}
      {errors?.options && (
        <div className="text-error text-sm bg-error/10 rounded p-3 border border-error/20">
          {errors?.options}
        </div>
      )}
      {/* Empty State */}
      {(!formData?.options || formData?.options?.length === 0) && (
        <div className="text-center py-8 bg-muted/50 rounded-lg border-2 border-dashed border-border">
          <Icon name="ShoppingCart" size={48} className="mx-auto text-text-secondary mb-4" />
          <p className="text-text-secondary">No options added yet</p>
          <p className="text-sm text-text-secondary mt-1">Use <strong>Find Options</strong> to discover products online, or add manually below</p>
        </div>
      )}
    </div>
  );
};

export default OptionsManagement;